import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'

import { LandingPage } from '@/components/landing-page'
import { Navbar } from '@/components/navbar'
import { buildAppHref, buildAuthSearch, clearAdminToken, getAuthFromSearch, type AuthMode } from '@/lib/auth'
import { AdminLayout } from '@/pages/admin/layout'
import { AdminOverviewPage } from '@/pages/admin/overview'
import { AdminDevicesPage } from '@/pages/admin/devices'
import { AdminLinksPage } from '@/pages/admin/links'
import { AdminTravelTimesPage } from '@/pages/admin/travel-times'
import { AdminSightingsPage } from '@/pages/admin/sightings'
import { AdminBaseTTPage } from '@/pages/admin/base-tt'
import { AdminBaseDataPage } from '@/pages/admin/base-data'
import { AdminDeviceDetailPage } from '@/pages/admin/device-detail'
import { AdminLinkDetailPage } from '@/pages/admin/link-detail'
import { HomePage } from '@/pages/home'
import { TravelTimePage } from '@/pages/travel-time'
import { DevicesPage } from '@/pages/devices'
import { ReportsPage } from '@/pages/reports'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 10000,
      retry: 1,
    },
  },
})

function ClientShell() {
  const location = useLocation()
  const navigate = useNavigate()
  const auth = getAuthFromSearch(location.search)

  function handleAuthenticated(mode: AuthMode, projectCode: string | null) {
    const pathname = mode === 'admin'
      ? '/admin'
      : location.pathname

    navigate(
      {
        pathname,
        search: buildAuthSearch({ mode, projectCode }),
      },
      { replace: true },
    )
  }

  function handleLogout() {
    clearAdminToken()
    navigate('/', { replace: true })
  }

  if (!auth) {
    return <LandingPage onAuthenticated={handleAuthenticated} />
  }

  // Admin users hitting client routes get redirected to admin shell
  if (auth.mode === 'admin' && !auth.projectCode) {
    return <Navigate to={buildAppHref('/admin', auth)} replace />
  }

  return (
    <div className="flex min-h-screen flex-col text-text">
      <Navbar auth={auth} onLogout={handleLogout} />
      <main className="mx-auto w-full max-w-[1200px] flex-1">
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/tt" element={<TravelTimePage />} />
          <Route path="/devices" element={<DevicesPage />} />
          <Route path="/reports" element={<ReportsPage />} />
          <Route path="*" element={<Navigate to={buildAppHref('/', auth)} replace />} />
        </Routes>
      </main>
    </div>
  )
}

function AppRouter() {
  return (
    <Routes>
      <Route path="/admin/*" element={<AdminLayout />}>
        <Route path="overview" element={<AdminOverviewPage />} />
        <Route path="devices" element={<AdminDevicesPage />} />
        <Route path="devices/:id" element={<AdminDeviceDetailPage />} />
        <Route path="links" element={<AdminLinksPage />} />
        <Route path="links/:id" element={<AdminLinkDetailPage />} />
        <Route path="travel-times" element={<AdminTravelTimesPage />} />
        <Route path="sightings" element={<AdminSightingsPage />} />
        <Route path="base-tt" element={<AdminBaseTTPage />} />
        <Route path="base-data" element={<AdminBaseDataPage />} />
      </Route>
      <Route path="/*" element={<ClientShell />} />
    </Routes>
  )
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
    </QueryClientProvider>
  )
}
